import { AlertCircle, CheckCircle, Mail, Shield, User, X } from 'lucide-react';
import { useState } from 'react';
import { RoleBadge } from './RoleBadge';

/**
 * User invitation interface
 */
interface UserInvitation {
	email: string;
	name: string;
	role: string;
	message?: string;
}

/**
 * Invite User Modal Props
 */
interface InviteUserModalProps {
	isOpen: boolean;
	onClose: () => void;
	onInvite: (invitation: UserInvitation) => Promise<void>;
}

/**
 * Invite User Modal Component
 */
export function InviteUserModal({ isOpen, onClose, onInvite }: InviteUserModalProps) {
	const [formData, setFormData] = useState<UserInvitation>({
		email: '',
		name: '',
		role: 'READ',
		message: '',
	});

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);

	const roles = [
		{ value: 'VIEWER', label: 'Viewer', description: 'Can view workflows only' },
		{ value: 'READ', label: 'Read', description: 'Can view workflows and executions' },
		{ value: 'EXECUTE', label: 'Execute', description: 'Can run workflows' },
		{ value: 'WRITE', label: 'Write', description: 'Can create and edit workflows' },
		{
			value: 'SUPPORT',
			label: 'Support',
			description: 'Can view detailed logs and retry executions',
		},
		{ value: 'ADMIN', label: 'Admin', description: 'Full system access' },
	];

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);

		try {
			// Validate email format
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (!emailRegex.test(formData.email)) {
				throw new Error('Please enter a valid email address');
			}

			// Validate email domain (vquip.com only)
			const vquipEmailRegex = /^[^\s@]+@vquip\.com$/;
			if (!vquipEmailRegex.test(formData.email)) {
				throw new Error('Email must be from vquip.com domain');
			}

			// Validate name
			if (!formData.name.trim()) {
				throw new Error("Please enter the user's full name");
			}

			await onInvite(formData);
			setSuccess(true);

			// Reset form after success
			setTimeout(() => {
				setFormData({ email: '', name: '', role: 'READ', message: '' });
				setSuccess(false);
				onClose();
			}, 2000);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to send invitation');
		} finally {
			setIsLoading(false);
		}
	};

	const handleClose = () => {
		if (!isLoading) {
			setFormData({ email: '', name: '', role: 'READ', message: '' });
			setError(null);
			setSuccess(false);
			onClose();
		}
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-50 overflow-y-auto">
			<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
				{/* Backdrop */}
				<div className="fixed inset-0 transition-opacity" onClick={handleClose}>
					<div className="absolute inset-0 bg-gray-500 opacity-75"></div>
				</div>

				{/* Modal */}
				<div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
					{/* Header */}
					<div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
						<div className="flex items-center justify-between mb-4">
							<div className="flex items-center">
								<div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900 sm:mx-0 sm:h-10 sm:w-10">
									<Mail className="h-6 w-6 text-blue-600 dark:text-blue-400" />
								</div>
								<div className="ml-4">
									<h3 className="text-lg font-medium text-gray-900 dark:text-white">
										Invite New User
									</h3>
									<p className="text-sm text-gray-500 dark:text-gray-400">
										Send an invitation to join your workspace
									</p>
								</div>
							</div>
							<button
								onClick={handleClose}
								disabled={isLoading}
								className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
							>
								<X className="h-6 w-6" />
							</button>
						</div>

						{/* Success Message */}
						{success && (
							<div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
								<div className="flex items-center">
									<CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
									<span className="text-sm text-green-800 dark:text-green-200">
										Invitation sent successfully!
									</span>
								</div>
							</div>
						)}

						{/* Error Message */}
						{error && (
							<div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
								<div className="flex items-center">
									<AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
									<span className="text-sm text-red-800 dark:text-red-200">
										{error}
									</span>
								</div>
							</div>
						)}

						{/* Form */}
						<form onSubmit={handleSubmit} className="space-y-4">
							{/* Email Field */}
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
									Email Address * (vquip.com domain only)
								</label>
								<div className="relative">
									<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
										<Mail className="h-5 w-5 text-gray-400" />
									</div>
									<input
										type="email"
										required
										value={formData.email}
										onChange={e =>
											setFormData({ ...formData, email: e.target.value })
										}
										className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
										placeholder="user@vquip.com"
										disabled={isLoading || success}
									/>
								</div>
							</div>

							{/* Name Field */}
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
									Full Name *
								</label>
								<div className="relative">
									<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
										<User className="h-5 w-5 text-gray-400" />
									</div>
									<input
										type="text"
										required
										value={formData.name}
										onChange={e =>
											setFormData({ ...formData, name: e.target.value })
										}
										className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
										placeholder="John Doe"
										disabled={isLoading || success}
									/>
								</div>
							</div>

							{/* Role Selection */}
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
									Initial Role *
								</label>
								<div className="relative">
									<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
										<Shield className="h-5 w-5 text-gray-400" />
									</div>
									<select
										value={formData.role}
										onChange={e =>
											setFormData({ ...formData, role: e.target.value })
										}
										className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
										disabled={isLoading || success}
									>
										{roles.map(role => (
											<option key={role.value} value={role.value}>
												{role.label} - {role.description}
											</option>
										))}
									</select>
								</div>
								<div className="mt-2">
									<RoleBadge role={formData.role} size="sm" showIcon={true} />
								</div>
							</div>

							{/* Optional Message */}
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
									Welcome Message (Optional)
								</label>
								<textarea
									value={formData.message}
									onChange={e =>
										setFormData({ ...formData, message: e.target.value })
									}
									rows={3}
									className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
									placeholder="Welcome to our workflow platform! You can start by..."
									disabled={isLoading || success}
								/>
							</div>
						</form>
					</div>

					{/* Footer */}
					<div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
						<button
							type="submit"
							onClick={handleSubmit}
							disabled={isLoading || success}
							className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{isLoading ? (
								<>
									<svg
										className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
									>
										<circle
											className="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											strokeWidth="4"
										></circle>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										></path>
									</svg>
									Sending...
								</>
							) : success ? (
								'Sent!'
							) : (
								'Send Invitation'
							)}
						</button>
						<button
							type="button"
							onClick={handleClose}
							disabled={isLoading}
							className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
						>
							Cancel
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
